import React from "react";

import { ReactComponent as CloseIcon } from "../../../../assets/svg/close.svg";
import { ReactComponent as RightIcon } from "../../../../assets/svg/arrow_right.svg";
import Dropdown, { Option } from "../../../../atoms/HintModal/Dropdown";
import { ReactComponent as PlusIcon } from "../../../../assets/svg/plus_icon.svg";
import { ReactComponent as WarningIcon } from "../../../../assets/svg/warning_red.svg";
import { ISubmitTeacherSurvey, ITeacherSurvey } from "../../../../state";
import {
  TEACHER_SURVEY_MODAL_PAGES,
  TeacherSurveyModalPage,
} from "../../../../constants/teaching_hint";

import styles from "./Form.module.scss";

const MAX_YEARS_OF_EXPERIENCE = 50;
const MONTHS_IN_YEAR = 12;
const MAX_SUBJECTS = 10;
const MAX_SCHOOL_EVENTS = 3;
const DEFAULT_EVENT = { name: "", description: "", month: undefined } as const;

const YEARS_OF_EXPERIENCE_OPTIONS: Option[] = Array.from(
  { length: MAX_YEARS_OF_EXPERIENCE },
  (_, i) => {
    const val = i + 1;
    return { value: val, label: val.toString() };
  },
);

const MONTH_OPTIONS: Option[] = Array.from(
  { length: MONTHS_IN_YEAR },
  (_, i) => {
    const val = i + 1;
    return { value: val, label: val.toString() };
  },
);

type FormData = {
  birth_date: string | null;
  years_of_teaching: number | null;
  interests: string;
  class_challenges: string;
  desired_student_outcomes: string;
  subjects: string[];
  school_events: ITeacherSurvey["school_events"];
};

type Props = {
  onClose: () => void;
  setModal: React.Dispatch<React.SetStateAction<TeacherSurveyModalPage>>;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  formData: ISubmitTeacherSurvey;
};

const Form: React.FC<Props> = ({
  onClose,
  setModal,
  setFormData,
  formData,
}) => {
  const [errors, setErrors] = React.useState({
    birth_date: false,
    interests: false,
    class_challenges: false,
    desired_student_outcomes: false,
  });

  const handleChange = <K extends keyof typeof formData>(
    key: K,
    value: typeof formData[K],
  ) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleChangeDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    inputValue = inputValue.replace(/[^0-9/]/g, "").slice(0, 10);

    const digitsOnly = inputValue.replace(/\//g, "");
    let formatted = "";
    for (let i = 0; i < digitsOnly.length; i++) {
      if (i === 4 || i === 6) formatted += "/";
      formatted += digitsOnly[i];
    }

    handleChange("birth_date", formatted || null);
  };

  const handleSubjectChange = (index: number, value: string) => {
    const updated = [...(formData.subjects ?? [])];
    updated[index] = value;
    handleChange("subjects", updated);
  };

  const handleAddSubject = () => {
    if ((formData.subjects?.length ?? 0) < MAX_SUBJECTS) {
      handleChange("subjects", [...(formData.subjects ?? []), ""]);
    }
  };

  const handleRemoveSubject = (index: number) => {
    const updated = [...(formData.subjects ?? [])];
    updated.splice(index, 1);
    handleChange("subjects", updated);
  };

  const handleSchoolEventChange = <
    K extends keyof ITeacherSurvey["school_events"][0]
  >(
    index: number,
    field: K,
    value: ITeacherSurvey["school_events"][0][K],
  ) => {
    const updated = [...(formData.school_events ?? [])];
    updated[index] = { ...updated[index], [field]: value };
    handleChange("school_events", updated);
  };

  const handleAddSchoolEvent = () => {
    if ((formData.school_events?.length ?? 0) < MAX_SCHOOL_EVENTS) {
      handleChange("school_events", [
        ...(formData.school_events ?? []),
        { name: "", description: "", month: undefined },
      ]);
    }
  };

  const handleRemoveSchoolEvent = (index: number) => {
    const updated = [...(formData.school_events ?? [])];
    updated.splice(index, 1);
    handleChange("school_events", updated);
  };

  const validateBirthDate = (dateString: string): boolean => {
    if (!dateString || dateString.length !== 10) return false;

    const dateRegex = /^\d{4}\/\d{2}\/\d{2}$/;
    if (!dateRegex.test(dateString)) return false;

    const [year, month, day] = dateString.split("/").map(Number);
    const date = new Date(year, month - 1, day);

    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  };

  const validateRequiredFields = () => {
    const validationResults = {
      birth_date: formData.birth_date
        ? validateBirthDate(formData.birth_date)
        : true,
      interests: formData.interests?.trim() !== "",
      class_challenges: formData.class_challenges?.trim() !== "",
      desired_student_outcomes:
        formData.desired_student_outcomes?.trim() !== "",
    };

    const errors = {
      birth_date: formData.birth_date ? !validationResults.birth_date : false,
      interests: !validationResults.interests,
      class_challenges: !validationResults.class_challenges,
      desired_student_outcomes: !validationResults.desired_student_outcomes,
    };

    return { isValid: Object.values(validationResults).every(Boolean), errors };
  };

  const handleProceedToConfirmation = () => {
    const { isValid, errors } = validateRequiredFields();
    setErrors(errors);

    if (isValid) {
      setModal(TEACHER_SURVEY_MODAL_PAGES.CONFIRMATION);
    }
  };

  const subjectsArray =
    formData.subjects?.length === 0 ? [""] : formData.subjects ?? [""];

  const schoolEventsArray =
    formData.school_events?.length === 0
      ? [DEFAULT_EVENT]
      : formData.school_events ?? [DEFAULT_EVENT];

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.leftPortion}>
          <h1 className={styles.headerText}>
            先生のこと、生徒のみなさんのことを教えてね！
          </h1>
        </div>
        <button className={styles.closeButton} onClick={onClose}>
          <CloseIcon />
        </button>
      </div>
      <div className={styles.content}>
        <p className={styles.title}>
          質問に詳しく答えるほど、先生にぴったりの授業アイデアを考えて提案するよ。
          <br />
          質問は全部で7つ、5〜10分で終わるよ。入力してもらってからヒントを考え始めるから、明日まで待っててね！
        </p>
        <p className={styles.requiredField}>*必須項目</p>

        <div className={styles.answerboxContainer}>
          <div className={styles.answerBox}>
            <div className={styles.header}>1.生年月日</div>
            <div className={styles.description}>
              生年月日を西暦で教えてください。(半角数字)
            </div>
            <div className={`pt-2 d-flex`}>
              <input
                type="text"
                value={formData.birth_date ?? ""}
                placeholder="YYYY/MM/DD"
                onChange={handleChangeDate}
                maxLength={10}
                className={`${styles.formInput} w-25`}
              />
            </div>
            {errors.birth_date && (
              <p className={styles.requiredFieldText}>
                生年月日を正しい形式で入力してください（例：1980/01/11）
              </p>
            )}
          </div>
          <div className={styles.answerBox}>
            <div className={styles.header}>2.教員歴</div>
            <div className={styles.description}>教員歴を教えてください。</div>
            <div
              className={`pt-2 d-flex justify-content-start align-items-center gap-2 `}
            >
              <Dropdown
                options={YEARS_OF_EXPERIENCE_OPTIONS}
                onChange={(value: number) =>
                  handleChange("years_of_teaching", value)
                }
                className="w-25"
                placeholder="選択"
                value={formData.years_of_teaching ?? undefined}
              />
              <p className={styles.prefixText}>年</p>
            </div>
          </div>
          <div className={styles.answerBox}>
            <div className={styles.header}>3.担当教科</div>
            <div className={styles.description}>
              ご担当されている教科を教えてください。
            </div>
            <div className={styles.description}>
              ※ご担当がない場合は未入力で問題ありません。
            </div>
            {subjectsArray.map((subject, index) => (
              <div className={`pt-2 d-flex position-relative`} key={index}>
                <input
                  type="text"
                  className={styles.subjectFormInput}
                  value={subject}
                  placeholder="例)国語"
                  onChange={e => handleSubjectChange(index, e.target.value)}
                />
                {index > 0 && (
                  <button
                    className={styles.subjectCancelBtn}
                    onClick={() => handleRemoveSubject(index)}
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
            ))}
            <div className={`pt-2 ${styles.buttonContainer}`}>
              <button className={styles.button} onClick={handleAddSubject}>
                <PlusIcon /> 教科を追加する
              </button>
            </div>
          </div>
          <div className={styles.answerBox}>
            <div className={styles.header}>
              4.先生や生徒の興味・関心<span className={styles.required}>*</span>
            </div>
            <div className={styles.description}>
              先生や生徒の「好きなこと」「興味・関心があること」「生徒たちがよく話題にすること」「最近流行っていること」などをなるべくたくさん教えてください。
            </div>
            <div className="pt-4">
              <textarea
                value={formData.interests}
                onChange={e => handleChange("interests", e.target.value)}
                rows={4}
                className={styles.textArea}
                placeholder="例)読書/キャンプ/お祭り/好きな映画やドラマ"
              />
            </div>
            {errors.interests && (
              <p className={styles.requiredFieldText}>
                先生や生徒の興味・関心を入力してください。
              </p>
            )}
          </div>
          <div className={styles.answerBox}>
            <div className={styles.header}>
              5.生徒に感じる課題<span className={styles.required}>*</span>
            </div>
            <div className={styles.description}>
              先生が受け持つクラスの生徒に対して感じている課題を教えてください。
            </div>
            <div className="pt-4">
              <textarea
                value={formData.class_challenges}
                onChange={e => handleChange("class_challenges", e.target.value)}
                rows={4}
                className={styles.textArea}
                placeholder="例)全体的におとなしく、積極的に発言する生徒が少ない印象です。"
              />
            </div>
            {errors.class_challenges && (
              <p className={styles.requiredFieldText}>
                生徒に感じる課題を入力してください。
              </p>
            )}
          </div>
          <div className={styles.answerBox}>
            <div className={styles.header}>
              6.理想の生徒像<span className={styles.required}>*</span>
            </div>
            <div className={styles.description}>
              ENAGEEDを通じて、先生が受け持つクラスの生徒にどのようになってほしいかを教えてください。
            </div>
            <div className="pt-4">
              <textarea
                value={formData.desired_student_outcomes}
                onChange={e =>
                  handleChange("desired_student_outcomes", e.target.value)
                }
                rows={4}
                className={styles.textArea}
                placeholder="例)自分の意見を持ち、それを相手に伝える力をつけてほしいと思っています。"
              />
            </div>
            {errors.desired_student_outcomes && (
              <p className={styles.requiredFieldText}>
                理想の生徒像を入力してください。
              </p>
            )}
          </div>
          <div className={styles.answerBox}>
            <div className={styles.header}>7.今年度のイベント</div>
            <div className={styles.description}>
              クラスや学校で予定しているイベントや行事を教えてください。
            </div>
            <div className={styles.description}>
              ※入力できるイベントは3つまでです。いつでも書き換え可能です。
            </div>
            {schoolEventsArray.map((event, index) => (
              <div className={`mt-3 ${styles.tabletSchool}`} key={index}>
                <div className={styles.eventRowContainer}>
                  <div className={styles.eventFieldsContainer}>
                    <div className={styles.eventInputGroup}>
                      <p
                        className={`${styles.eventText} ${
                          index === 0 ? "" : styles.eventTextHide
                        }`}
                      >
                        イベント名
                      </p>

                      <input
                        type="text"
                        className={styles.formInput}
                        placeholder="例)文化祭"
                        value={event.name}
                        onChange={e =>
                          handleSchoolEventChange(index, "name", e.target.value)
                        }
                      />
                    </div>
                    <div className={styles.eventInputGroup}>
                      <p
                        className={`${styles.eventText} ${
                          index === 0 ? "" : styles.eventTextHide
                        }`}
                      >
                        概要(任意)
                      </p>

                      <input
                        type="text"
                        className={styles.formInput}
                        placeholder="例)イベント名が学校特有の場合補足を記載。"
                        value={event.description}
                        onChange={e =>
                          handleSchoolEventChange(
                            index,
                            "description",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                    <div className={styles.eventInputGroup}>
                      <p
                        className={`${styles.eventText} ${
                          index === 0 ? "" : styles.eventTextHide
                        }`}
                      >
                        実施月
                      </p>

                      <Dropdown
                        options={MONTH_OPTIONS}
                        onChange={(value: number) =>
                          handleSchoolEventChange(index, "month", value)
                        }
                        className={styles.tabletDropdown}
                        placeholder="◯月"
                        value={event.month}
                      />
                    </div>
                  </div>
                  <div className={styles.eventButtonContainer}>
                    {index > 0 ? (
                      <button
                        className={`${styles.cancelBtn} ${styles.small}`}
                        onClick={() => handleRemoveSchoolEvent(index)}
                      >
                        <CloseIcon />
                      </button>
                    ) : (
                      <div className={styles.spacer} />
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div className={`pt-4 ${styles.buttonContainer}`}>
              <button
                className={styles.button}
                disabled={
                  (formData.school_events?.length ?? 0) >= MAX_SCHOOL_EVENTS
                }
                onClick={handleAddSchoolEvent}
              >
                <PlusIcon /> イベントを追加する
              </button>
            </div>
          </div>
        </div>
        {Object.values(errors).some(Boolean) && (
          <div className={styles.errorMessage}>
            <WarningIcon />
            入力エラーがあります。
          </div>
        )}
        <div className={styles.buttonContainer}>
          <button
            className={styles.submitButton}
            onClick={handleProceedToConfirmation}
          >
            入力項目の確認に進む <RightIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Form;
